import { PropsWithChildren, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useFilterContext } from 'src/contexts/FilterContext';
import { useForecastComponentsQuery, useForecastsQuery } from 'src/api/queries';
import {
  CreateForecastPayload,
  useCreateForecastMutation,
  useDeleteForecastMutation,
} from 'src/api/mutations';
import { ForecastingDataContext, ForecastingDataContextValue } from '.';

export function ForecastingDataContextProvider(props: PropsWithChildren) {
  const { subscriptionLevels, currentCustomer } = useAppContext();
  const { currentModelVersion } = useFilterContext();
  const queryClient = useQueryClient();

  const customer = currentCustomer?.customerName ?? '';
  const modelBuild = currentModelVersion?.buildID ?? '';
  const modelId = currentModelVersion?.modelID ?? '';
  const enabled = !!(subscriptionLevels.mo && currentModelVersion && customer);

  const queryParams = { customer, model_build: modelBuild, model_id: modelId };

  const { data: componentsData, isLoading: isCheckingComponents } =
    useForecastComponentsQuery(queryParams, enabled);

  const {
    data: forecasts,
    isLoading: isForecastsLoading,
    isError: isForecastsError,
  } = useForecastsQuery(queryParams, enabled);

  const createMutation = useCreateForecastMutation();
  const deleteMutation = useDeleteForecastMutation();

  const createForecast = useCallback(
    async (data: CreateForecastPayload) => {
      await createMutation.mutateAsync(
        {
          customer,
          modelBuild,
          modelId,
          ...data,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['useForecastsQuery']);
          },
        },
      );
    },
    [customer, modelBuild, modelId, createMutation, queryClient],
  );

  const deleteForecast = useCallback(
    async (forecastId: string) => {
      await deleteMutation.mutateAsync(
        { customer, forecastId },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['useForecastsQuery']);
          },
        },
      );
    },
    [customer, deleteMutation, queryClient],
  );

  const value: ForecastingDataContextValue = {
    forecasts: {
      data: forecasts,
      isLoading: isForecastsLoading,
      isError: isForecastsError,
    },
    forecastComponentsAvailable: componentsData?.available ?? false,
    baselineSpend: componentsData?.baselineSpend ?? {},
    isCheckingComponents,
    createForecast,
    deleteForecast,
  };

  return (
    <ForecastingDataContext.Provider value={value}>
      {props.children}
    </ForecastingDataContext.Provider>
  );
}
